import { configureStore, type Middleware } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { mApi } from "@api/mApi";
import { pApi } from "@api/pApi";
import { unitsApi } from "@units/unitsApi";
import { prefsReducer } from "./slices/prefsSlice";
import { wellContextReducer } from "./slices/wellContextSlice";
import {
  saveDisplayCurrency,
  saveDisplayProfile,
  saveLocale,
  saveNavCollapsed,
  saveTenant,
  saveTheme,
} from "@lib/preferences";

/**
 * Persist theme / tenant / locale back to the preference store whenever the
 * prefs slice changes. Kept out of the reducer so the store stays pure.
 */
const persistMiddleware: Middleware = (store) => (next) => (action) => {
  const before = (store.getState() as RootState).prefs;
  const result = next(action);
  const after = (store.getState() as RootState).prefs;
  if (before !== after) {
    if (before.theme !== after.theme) saveTheme(after.theme);
    if (before.tenant !== after.tenant) saveTenant(after.tenant);
    if (before.locale !== after.locale) saveLocale(after.locale);
    if (before.navCollapsed !== after.navCollapsed) saveNavCollapsed(after.navCollapsed);
    if (before.displayProfile !== after.displayProfile) saveDisplayProfile(after.displayProfile);
    if (before.displayCurrency !== after.displayCurrency)
      saveDisplayCurrency(after.displayCurrency);
  }
  return result;
};

export const store = configureStore({
  reducer: {
    [mApi.reducerPath]: mApi.reducer,
    [pApi.reducerPath]: pApi.reducer,
    [unitsApi.reducerPath]: unitsApi.reducer,
    prefs: prefsReducer,
    wellContext: wellContextReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      mApi.middleware,
      pApi.middleware,
      unitsApi.middleware,
      persistMiddleware,
    ),
});

setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
